import { useEffect, useState } from "react";
import { Table } from "@cofano/cck";
import { SelectSettingsProps } from "@cofano/cck/Table/Table";
import { intl } from "LocaleWrapper";
import { DataFormat, usePermission, hasPluginPermission } from "imports/Utils";
import { DossierLink, RelationLink } from "utils/LinkUtils";
import OverflowTooltip from "components/cck/OverflowTooltip";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import StatusChip from "view/statuses/components/changer/StatusChip";
import { AgreementTypeEnum, StatusType } from "__generated__/enums";
import { FormattedDate } from "utils/DateUtils";
import { Box, IconButton, Tooltip } from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import CheckIcon from "@mui/icons-material/DoneAll";
import { useConfirm } from "components/hooks/useConfirm";
import InvoiceLineEditDialog from "view/invoicing/components/dialogs/InvoiceLineEditDialog";
import ApproveInvoiceLineDialog from "view/invoicing/components/dialogs/ApproveInvoiceLineDialog";
import { useMutation } from "@apollo/client";
import { RevokeInvoiceLineApprovalDocument, DeleteInvoiceLineDocument } from "__generated__/view/invoicing/graphql/invoicing.mutations.generated";
import { useStoreState } from "ezStore";
import { InvoiceLineTableRowFragment } from "__generated__/view/declarations/graphql/declaration.fragments.generated";

type InvoiceLineRow = InvoiceLineTableRowFragment & {
	undeclared?: boolean;
};

export type DeclarationTableProps = {
	declaration?: {
		id: number;
		closedOn?: number | null;
		financiallyReadiedOn?: number | null;
		invoiceLines?: (InvoiceLineRow | null)[] | null;
	};
	lines?: (InvoiceLineRow | null)[] | null;
	mutedAgreements?: {
		id: number;
		type: AgreementTypeEnum;
		calculationMethod?: { summary?: string | null } | null;
	}[];
	loading?: boolean;
	refetch?: () => void;
	selectSettings?: SelectSettingsProps;
};

const InvoiceLinesTable = ({
	declaration,
	lines,
	mutedAgreements,
	loading,
	refetch: refetchProp,
	selectSettings,
}: DeclarationTableProps) => {

	const { isReadOnly } = usePermission();
	const confirm = useConfirm();

	const session = useStoreState(store => store.session);
	const { locale } = session;
	const disableInvoiceLineEditing = hasPluginPermission(session, "disableInvoiceLineEditing");
	const [ tableVersion, setTableVersion ] = useState(0);

	const refetch = () => {
		refetchProp?.();
		setTableVersion(v => v + 1);
	};

	const rawLines = declaration?.invoiceLines ?? lines ?? [];
	useEffect(() => {
		setTableVersion(v => v + 1);
	}, [ rawLines ]);

	const [ invoiceLineDialog, setInvoiceLineDialog ] = useState<{ open: boolean; invoiceLine: InvoiceLineRow | null }>({
		open: false,
		invoiceLine: null,
	});

	const [ approveDialog, setApproveDialog ] = useState<{ open: boolean; invoiceLine: InvoiceLineRow | null }>({
		open: false,
		invoiceLine: null,
	});

	const [ revokeApproval ] = useMutation(RevokeInvoiceLineApprovalDocument, {
		onCompleted: () => refetch(),
	});

	const [ deleteInvoiceLine ] = useMutation(DeleteInvoiceLineDocument, {
		onCompleted: () => refetch(),
	});

	const invoiceLines = rawLines.filter((line): line is InvoiceLineRow => line != null);

	const mutedRows: InvoiceLineRow[] = (mutedAgreements ?? []).map(a => ({
		id: -a.id,
		undeclared: true,
		agreement: a,
	}));
	const content = [ ...invoiceLines, ...mutedRows ];

	const locked = !!declaration?.closedOn || !!declaration?.financiallyReadiedOn;

	return <>
		<Table
			id="invoicelinestable"
			key={tableVersion}
			contentLoading={loading}
			content={content}
			selectSettings={selectSettings}
			rowActions={refetchProp && !locked ? [ {
				icon: <></>,
				onClick: () => undefined,
				hidden: (row: InvoiceLineRow) => isReadOnly || disableInvoiceLineEditing || !!row.undeclared,
				render: (row: InvoiceLineRow) => row.approved ? (
					<Tooltip title={intl.formatMessage({ id: "INVOICE_LINE_WITHDRAW_APPROVAL" })}>
						<IconButton
							size="small"
							onClick={() => revokeApproval({ variables: { invoiceLineIds: [ row.id ] } })}>
							<RotateLeftIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				) : (
					<Tooltip title={intl.formatMessage({ id: "INVOICE_LINES_APPROVE" })}>
						<IconButton
							size="small"
							onClick={() => setApproveDialog({ open: true, invoiceLine: row })}>
							<CheckIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				),
			}, {
				icon: <Edit fontSize="small" />,
				tooltip: intl.formatMessage({ id: "EDIT" }),
				hidden: (row: InvoiceLineRow) => isReadOnly || disableInvoiceLineEditing || !!row.undeclared,
				disabled: (row: InvoiceLineRow) => !!row.approved,
				onClick: (row: InvoiceLineRow) => {
					setInvoiceLineDialog({ open: true, invoiceLine: row });
				},
			}, {
				icon: <DeleteOutline fontSize="small" />,
				tooltip: intl.formatMessage({ id: "DELETE" }),
				hidden: (row: InvoiceLineRow) => isReadOnly || disableInvoiceLineEditing || !!row.undeclared,
				disabled: (row: InvoiceLineRow) => !!row.approved,
				onClick: (row: InvoiceLineRow) => {
					confirm({ type: "DELETE" }).then(() => {
						deleteInvoiceLine({
							variables: {
								id: row.id,
							},
						});
					});
				},
			} ] : undefined}
			columns={[
				{
					id: "approvedIndicator",
					title: "",
					defaultPinned: true,
					disableMoveable: true,
					disablePinnable: true,
					disableVisibility: true,
					disableFlexGrow: true,
					disableResizing: true,
					disabled: disableInvoiceLineEditing,
					width: 32,
					render: (row: InvoiceLineRow) => {
						if (row.undeclared) {
							return null;
						}
						const color = row.approved ? "#45B741" : "#999999";
						const title = intl.formatMessage({ id: row.approved ? "APPROVED" : "NOT_YET_APPROVED" });

						return (
							<Tooltip title={title} placement="top" arrow>
								<Box
									component="span"
									sx={{
										display: "inline-block",
										width: 14,
										height: 14,
										borderRadius: "50%",
										bgcolor: color,
										verticalAlign: "middle",
									}}
								/>
							</Tooltip>
						);
					},
				}, {
					id: "charter",
					title: intl.formatMessage({ id: "CHARTER" }),
					render: (row: InvoiceLineRow) => <DossierLink charter={row.declaration?.charter} />,
					disabled: !!declaration,
				}, {
					id: "declarationStatus",
					title: intl.formatMessage({ id: "DECLARATION_STATUS" }),
					render: (row: InvoiceLineRow) => row.declaration?.lastStatusChange ? (
						<StatusChanger
							statusType={StatusType.Declaration}
							entity={row.declaration}
							refetch={() => refetch?.()}
							showHistory />
					) : null,
					disabled: !!declaration,
				}, {
					id: "blDate",
					title: intl.formatMessage({ id: "BL_DATE" }),
					render: (row: InvoiceLineRow) => row.declaration?.charter && "blDate" in row.declaration.charter
						? <FormattedDate date={row.declaration.charter.blDate} format="ll" />
						: null,
					disabled: !!declaration,
				}, {
					id: "charterStatus",
					title: `${intl.formatMessage({ id: "CHARTER" })} ${intl.formatMessage({ id: "STATUS" }).toLowerCase()}`,
					render: (row: InvoiceLineRow) => row.declaration?.charter && "status" in row.declaration.charter ? (
						<StatusChip
							label={intl.formatMessage({ id: `${row.declaration.charter.status.languageKeyPrefix}${row.declaration.charter.status.name}` })}
							color={row.declaration.charter.status.color} />
					) : null,
					disabled: !!declaration,
				}, {
					id: "agreement",
					title: intl.formatMessage({ id: "AGREEMENT" }),
					width: 150,
					disableFlexGrow: true,
					render: (row: InvoiceLineRow) => <OverflowTooltip>
						<Box color={row.undeclared ? "grey" : undefined}>
							{row.agreement
								? intl.formatMessage({ id: `AGREEMENT_TYPE_${row.agreement?.type}` })
								: row.costType ? DataFormat.costType(row.costType, locale) : undefined
							}
						</Box>
					</OverflowTooltip>,
				}, {
					id: "costType",
					title: intl.formatMessage({ id: "COST_TYPE" }),
					defaultVisible: false,
					render: (row: InvoiceLineRow) => row.costType ? DataFormat.costType(row.costType, locale) : undefined,
				}, {
					id: "agreement.description",
					title: intl.formatMessage({ id: "DESCRIPTION" }),
					render: (row: InvoiceLineRow) =>
						<OverflowTooltip>
							<Box color={row.undeclared ? "grey" : undefined}>
								{row.description || row.agreement?.calculationMethod?.summary}
							</Box>
						</OverflowTooltip>,
				}, {
					id: "calculation",
					title: intl.formatMessage({ id: "CALCULATION" }),
					render: (row: InvoiceLineRow) => <OverflowTooltip>{row.calculationSummary}</OverflowTooltip>,
				}, {
					id: "totalPriceExcVAT",
					title: intl.formatMessage({ id: "TOTAL_EXCLUDE_VAT" }),
					render: (row: InvoiceLineRow) => row.undeclared ? null : DataFormat.price(row.totalPriceExcVAT),
				}, {
					id: "totalPriceIncVAT",
					title: intl.formatMessage({ id: "TOTAL_INCLUDE_VAT" }),
					render: (row: InvoiceLineRow) => row.undeclared ? null : DataFormat.price(row.totalPriceIncVAT),
					defaultVisible: false,
				}, {
					id: "debtor",
					title: intl.formatMessage({ id: "DEBTOR" }),
					render: (row: InvoiceLineRow) => <RelationLink relation={row.debtor} />,
					defaultVisible: false,
				}, {
					id: "creditor",
					title: intl.formatMessage({ id: "CREDITOR" }),
					render: (row: InvoiceLineRow) => <RelationLink relation={row.creditor} />,
					defaultVisible: false,
				}, {
					id: "invoiceReference",
					title: intl.formatMessage({ id: "INVOICE_REFERENCE" }),
					defaultVisible: false,
					render: (row: InvoiceLineRow) => <OverflowTooltip>{row.invoiceReference}</OverflowTooltip>,
				}, {
					id: "unexpected",
					title: intl.formatMessage({ id: "UNEXPECTED" }),
					render: (_row: InvoiceLineRow, field: InvoiceLineRow["unexpected"]) => DataFormat.boolean(field ? true : undefined),
					defaultVisible: false,
				} ]}
		/>

		<InvoiceLineEditDialog
			open={invoiceLineDialog.open}
			onClose={() => setInvoiceLineDialog({ open: false, invoiceLine: null })}
			invoiceLine={invoiceLineDialog.invoiceLine}
			refetch={refetch}
		/>

		<ApproveInvoiceLineDialog
			open={approveDialog.open}
			onClose={() => setApproveDialog({ open: false, invoiceLine: null })}
			invoiceLineIds={approveDialog.invoiceLine?.id ? [ approveDialog.invoiceLine.id ] : []}
			initialReference={approveDialog.invoiceLine?.invoiceReference}
			refetch={refetch}
		/>
	</>;
};

export default InvoiceLinesTable;
